import React from 'react';
import { Logo } from './Logo';
import { 
    SparklesIcon, 
    HeartIcon, 
    MoonIcon, 
    UsersIcon, 
    StarIcon, 
    ShieldIcon, 
    LockIcon, 
    PhoneIcon, 
    MessageCircleIcon,
    ChevronRightIcon,
    CreditCardIcon,
    UserCheckIcon,
    CardsIcon,
    MailIcon
} from './Icons';
import { useClient } from '../contexts/ClientContext';

interface MobileMenuPageProps {
    onNavigate: (page: string) => void;
    onOpenSarah?: () => void;
    onOpenAuth?: () => void;
    onOpenProfile?: () => void;
}

export const MobileMenuPage: React.FC<MobileMenuPageProps> = ({
    onNavigate,
    onOpenSarah,
    onOpenAuth,
    onOpenProfile
}) => {
    const { client, logoutClient } = useClient();

    const menuSections = [
        {
            title: "Services & Outils de Divination",
            items: [
                {
                    id: 'mediums',
                    title: 'Nos Médiums',
                    subtitle: 'Consultez nos voyants purs, tarologues & astrologues en direct',
                    icon: <UsersIcon className="w-5 h-5 text-purple-300" />,
                    gradient: 'from-purple-900/60 to-indigo-950/60 border-purple-500/30 text-purple-300'
                },
                {
                    id: 'tarot',
                    title: 'Tirage de Tarot',
                    subtitle: 'Tirage interactif 3 cartes, Amour, Avenir & Guidance',
                    icon: <CardsIcon className="w-5 h-5 text-amber-300" />,
                    gradient: 'from-amber-950/60 to-purple-950/60 border-amber-500/30 text-amber-300'
                },
                {
                    id: 'love',
                    title: 'Love Match',
                    subtitle: 'Calculez votre compatibilité amoureuse & affinités célestes',
                    icon: <HeartIcon className="w-5 h-5 text-pink-300 fill-pink-400/20" />,
                    gradient: 'from-pink-950/60 to-rose-950/60 border-pink-500/30 text-pink-300'
                },
                {
                    id: 'dreams',
                    title: 'Explication des Rêves',
                    subtitle: 'Découvrez la signification cachée de vos visions nocturnes',
                    icon: <MoonIcon className="w-5 h-5 text-indigo-300" />,
                    gradient: 'from-indigo-950/60 to-blue-950/60 border-indigo-500/30 text-indigo-300'
                },
                {
                    id: 'numerology',
                    title: 'Numérologie',
                    subtitle: 'Révélez votre chemin de vie et vos cycles karmiques',
                    icon: <SparklesIcon className="w-5 h-5 text-cyan-300" />,
                    gradient: 'from-cyan-950/60 to-indigo-950/60 border-cyan-500/30 text-cyan-300'
                },
                {
                    id: 'crystal',
                    title: 'Boule de Cristal',
                    subtitle: 'Flash médiumnique et réponses instantanées par les astres',
                    icon: <SparklesIcon className="w-5 h-5 text-fuchsia-300" />,
                    gradient: 'from-fuchsia-950/60 to-purple-950/60 border-fuchsia-500/30 text-fuchsia-300'
                }
            ]
        },
        {
            title: "Communauté & Échanges",
            items: [
                {
                    id: 'forum',
                    title: 'Forums Astrox',
                    subtitle: '+10 000 membres actifs : partagez vos histoires et ressenti',
                    icon: <MessageCircleIcon className="w-5 h-5 text-purple-200" />,
                    gradient: 'from-indigo-950/60 to-purple-950/60 border-purple-500/30 text-purple-200'
                },
                {
                    id: 'messages',
                    title: 'Mes Messages Privés',
                    subtitle: 'Boîte de réception & conversations avec vos voyants',
                    icon: <MailIcon className="w-5 h-5 text-violet-200" />,
                    gradient: 'from-violet-950/60 to-fuchsia-950/60 border-violet-500/30 text-violet-200'
                }
            ]
        },
        {
            title: "Tarifs & Support Privé",
            items: [
                {
                    id: 'tarifs',
                    title: 'Nos Tarifs & Recharges Astres',
                    subtitle: 'Forfaits clairs, Transcash 1€ = 10 Astres & Audiotel',
                    icon: <CreditCardIcon className="w-5 h-5 text-gold-300" />,
                    gradient: 'from-amber-950/60 to-yellow-950/60 border-gold-500/40 text-gold-300'
                },
                {
                    id: 'sarah',
                    title: 'Assistance Secrétariat (Sarah)',
                    subtitle: 'Facturation, activation de recharges & accompagnement',
                    icon: <UserCheckIcon className="w-5 h-5 text-rose-200" />,
                    gradient: 'from-rose-950/60 to-purple-950/60 border-rose-500/30 text-rose-200',
                    action: onOpenSarah
                }
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-[#08081A] text-white pt-6 pb-28 px-4 max-w-2xl mx-auto animate-fade-in">
            {/* Header du Menu */}
            <div className="text-center mb-6 pb-4 border-b border-purple-900/40">
                <Logo size="md" showTagline={true} onClick={() => onNavigate('home')} />
                <p className="text-xs text-purple-300/80 mt-2">
                    Menu Général de Navigation & Services
                </p>
            </div>

            {/* Carte Profil Rapide */}
            <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-purple-950/80 via-slate-900/90 to-purple-950/80 border border-gold-500/30 shadow-xl flex items-center justify-between">
                {client ? (
                    <div className="flex items-center gap-3 min-w-0" onClick={onOpenProfile || (() => onNavigate('profile'))}>
                        <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-gold-500 to-amber-600 flex items-center justify-center text-black font-black text-lg border-2 border-gold-400 shrink-0">
                            {client.avatar ? (
                                <img src={client.avatar} alt={client.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                                client.name.charAt(0).toUpperCase()
                            )}
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-sm font-bold text-white truncate flex items-center gap-1.5">
                                <span>{client.name}</span>
                                <span className="text-[10px] bg-gold-500/20 text-gold-300 border border-gold-500/40 px-2 py-0.2 rounded-full font-extrabold">
                                    {client.loyaltyTier || 'Bronze'}
                                </span>
                            </h3>
                            <p className="text-xs text-gold-400 font-semibold flex items-center gap-1 mt-0.5">
                                {client.loyaltyPoints || 0} Astres disponibles
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-purple-900/50 border border-purple-500/40 flex items-center justify-center text-xl shrink-0">
                            
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-white">Espace Client Privé</h3>
                            <p className="text-xs text-purple-300">Connectez-vous pour retrouver vos voyances</p>
                        </div>
                    </div>
                )}

                <div>
                    {client ? (
                        <button
                            onClick={onOpenProfile || (() => onNavigate('profile'))}
                            className="bg-gold-500 hover:bg-gold-400 text-black text-xs font-black px-3.5 py-2 rounded-xl shadow-md transition-all active:scale-95 cursor-pointer whitespace-nowrap"
                        >
                            Mon Compte
                        </button>
                    ) : (
                        <button
                            onClick={onOpenAuth}
                            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-bold px-3.5 py-2 rounded-xl shadow-md transition-all active:scale-95 cursor-pointer whitespace-nowrap border border-purple-400/40"
                        >
                            Se Connecter
                        </button>
                    )}
                </div>
            </div>

            {/* Sections du Menu */}
            <div className="space-y-6">
                {menuSections.map((section, sIdx) => (
                    <div key={sIdx} className="space-y-2.5">
                        <h4 className="text-xs font-black uppercase tracking-wider text-amber-300/90 px-1">
                            {section.title}
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {section.items.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => {
                                        if (item.action) item.action();
                                        else onNavigate(item.id);
                                    }}
                                    className={`text-left p-3.5 rounded-2xl bg-gradient-to-br ${item.gradient} border backdrop-blur-md hover:border-gold-400/60 shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-98 flex items-center justify-between gap-3 group cursor-pointer`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className="text-2xl shrink-0 group-hover:scale-110 transition-transform">
                                            {item.icon}
                                        </span>
                                        <div className="min-w-0">
                                            <h5 className="text-sm font-bold text-white group-hover:text-amber-200 transition-colors truncate">
                                                {item.title}
                                            </h5>
                                            <p className="text-[11px] text-purple-200/70 leading-snug line-clamp-2 mt-0.5">
                                                {item.subtitle}
                                            </p>
                                        </div>
                                    </div>
                                    <ChevronRightIcon className="w-4 h-4 text-purple-400 group-hover:text-amber-300 shrink-0 transition-transform group-hover:translate-x-1" />
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Numéro Audiotel Direct */}
            <div className="mt-8 p-4 rounded-2xl bg-gradient-to-r from-emerald-950/80 via-black/80 to-emerald-950/80 border border-emerald-500/40 text-center shadow-xl">
                <p className="text-xs text-emerald-300 font-bold uppercase tracking-wider mb-1">
                    Consultation Directe Immédiate par Téléphone
                </p>
                <a 
                    href="tel:0985405274"
                    className="text-xl sm:text-2xl font-black text-white hover:text-emerald-300 tracking-wider font-mono transition-colors block"
                >
                    09 85 40 52 74
                </a>
                <p className="text-[10px] text-emerald-400/80 mt-1">
                    Service direct sans attente • 0,80 € / min • 7j/7 & 24h/24
                </p>
            </div>

            {/* Footer Menu */}
            <div className="mt-6 text-center text-xs text-purple-400/60 space-y-2">
                <p>© {new Date().getFullYear()} Astrox.fr — Tous droits réservés.</p>
                {client && (
                    <button
                        onClick={logoutClient}
                        className="text-red-400 hover:text-red-300 text-xs font-bold underline cursor-pointer"
                    >
                        Se déconnecter de mon compte
                    </button>
                )}
            </div>
        </div>
    );
};

export default MobileMenuPage;
